<script setup lang="ts">
import { ref, onMounted, computed } from 'vue'
import PhoneInput from '@/Components/Forms/PhoneInput.vue'
import StudentSettingsLayout from '@/Layouts/StudentSettingsLayout.vue'
import ProfilePictureCropModal from '@/Components/Modals/ProfilePictureCropModal.vue'
import { Camera, Trash2, User } from 'lucide-vue-next'

interface UserData {
  id: number
  name: string
  email: string
  phone: string | null
  date_of_birth: string | null
  avatar_url: string | null
  timezone: string | null
}

const props = defineProps<{
  user: UserData
  photoUrl: string | null
}>()

const form = ref({
  first_name: '',
  last_name: '',
  phone: '',
  date_of_birth: '',
  timezone: 'Europe/London',
})

const avatarPreview = ref<string | null>(null)
const photoRemoved = ref(false)
const isUploading = ref(false)
const isSaving = ref(false)
const isRemovingPhoto = ref(false)
const successMessage = ref('')
const errorMessage = ref('')
const errors = ref<Record<string, string[]>>({})
const fileInputRef = ref<HTMLInputElement | null>(null)

// Crop modal state
const showCropModal = ref(false)
const selectedFile = ref<File | null>(null)

const timezones = [
  'Europe/London',
  'Europe/Paris',
  'Europe/Berlin',
  'America/New_York',
  'America/Los_Angeles',
  'Asia/Tokyo',
  'Asia/Singapore',
  'Australia/Sydney',
]

const currentAvatar = computed(() => {
  if (photoRemoved.value) return null
  return avatarPreview.value || props.photoUrl || null
})

onMounted(() => {
  if (props.user) {
    const nameParts = (props.user.name || '').split(' ')
    form.value = {
      first_name: nameParts[0] || '',
      last_name: nameParts.slice(1).join(' ') || '',
      phone: props.user.phone || '',
      date_of_birth: props.user.date_of_birth?.split('T')[0] || '',
      timezone: props.user.timezone || 'Europe/London',
    }
  }
})

function triggerFileInput() {
  fileInputRef.value?.click()
}

function handleFileSelect(event: Event) {
  const input = event.target as HTMLInputElement
  const file = input.files?.[0]

  if (!file) return

  // Validate file type
  const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp']
  if (!allowedTypes.includes(file.type)) {
    errorMessage.value = 'Please select a JPG, PNG, or WebP image'
    return
  }

  // Validate file size (max 5MB)
  if (file.size > 5 * 1024 * 1024) {
    errorMessage.value = 'Image must be less than 5MB'
    return
  }

  // Open crop modal
  selectedFile.value = file
  showCropModal.value = true

  // Reset file input
  if (input) input.value = ''
}

function closeCropModal() {
  showCropModal.value = false
  selectedFile.value = null
}

async function handleCroppedUpload(blob: Blob, originalFileName: string) {
  isUploading.value = true
  errorMessage.value = ''

  try {
    const contentType = blob.type || 'image/jpeg'

    // Step 1: Get presigned upload URL from backend
    const { data: presignedData } = await window.axios.post('/student/settings/profile/presigned-upload-url', {
      filename: originalFileName,
      content_type: contentType,
    })

    if (!presignedData.success) {
      throw new Error(presignedData.message || 'Failed to get upload URL')
    }

    const { upload_url, public_url } = presignedData.data

    // Step 2: Upload directly to R2 using presigned URL (external, uses fetch)
    const uploadResponse = await fetch(upload_url, {
      method: 'PUT',
      headers: {
        'Content-Type': contentType,
      },
      body: blob,
    })

    if (!uploadResponse.ok) {
      throw new Error('Failed to upload to storage')
    }

    // Step 3: Save the public URL to the backend
    await window.axios.put('/student/settings/profile/photo', {
      photo_path: public_url,
    })

    // Update preview
    avatarPreview.value = public_url
    photoRemoved.value = false
    successMessage.value = 'Profile photo updated successfully!'
    setTimeout(() => { successMessage.value = '' }, 3000)

    // Close modal
    closeCropModal()
  } catch (error: any) {
    errorMessage.value = error.response?.data?.message || error.message || 'Failed to upload photo'
  } finally {
    isUploading.value = false
  }
}

async function removePhoto() {
  if (!currentAvatar.value) return

  isRemovingPhoto.value = true
  errorMessage.value = ''

  try {
    await window.axios.delete('/student/settings/profile/photo')

    avatarPreview.value = null
    photoRemoved.value = true
    successMessage.value = 'Profile photo removed'
    setTimeout(() => { successMessage.value = '' }, 3000)
  } catch (error: any) {
    errorMessage.value = error.response?.data?.message || error.message || 'Failed to remove photo'
  } finally {
    isRemovingPhoto.value = false
  }
}

async function saveProfile() {
  isSaving.value = true
  errors.value = {}
  errorMessage.value = ''

  try {
    await window.axios.put('/student/settings/profile', form.value)

    successMessage.value = 'Profile updated successfully!'
    setTimeout(() => { successMessage.value = '' }, 3000)
  } catch (error: any) {
    if (error.response?.data?.errors) {
      errors.value = error.response.data.errors
    } else {
      errorMessage.value = error.response?.data?.message || error.message || 'An error occurred'
    }
  } finally {
    isSaving.value = false
  }
}
</script>

<template>
  <StudentSettingsLayout>
    <div class="space-y-6">
      <!-- Success Message -->
      <div v-if="successMessage" class="bg-green-50 border-2 border-green-200 rounded-2xl p-5 flex items-center">
        <div class="flex-shrink-0 w-10 h-10 bg-green-500 rounded-full flex items-center justify-center mr-4">
          <svg class="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"></path>
          </svg>
        </div>
        <div>
          <p class="text-green-900 font-semibold">Success</p>
          <p class="text-green-700 text-sm">{{ successMessage }}</p>
        </div>
      </div>

      <!-- Error Message -->
      <div v-if="errorMessage" class="bg-red-50 border-2 border-red-200 rounded-2xl p-5 flex items-center">
        <div class="flex-shrink-0 w-10 h-10 bg-red-500 rounded-full flex items-center justify-center mr-4">
          <svg class="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
          </svg>
        </div>
        <div>
          <p class="text-red-900 font-semibold">Error</p>
          <p class="text-red-700 text-sm">{{ errorMessage }}</p>
        </div>
      </div>

      <!-- Profile Photo Section -->
      <section class="bg-white rounded-2xl shadow-sm p-8">
        <h2 class="text-xl font-semibold text-gray-900 mb-6">Profile Photo</h2>

        <div class="flex items-center gap-6">
          <!-- Avatar Preview -->
          <div class="relative">
            <div
              :class="[
                'w-24 h-24 rounded-full overflow-hidden bg-gray-100 flex items-center justify-center',
                isUploading && 'opacity-50'
              ]"
            >
              <img
                v-if="currentAvatar"
                :src="currentAvatar"
                alt="Profile photo"
                class="w-full h-full object-cover"
              />
              <User v-else class="w-10 h-10 text-gray-400" />
            </div>
            <div
              v-if="isUploading"
              class="absolute inset-0 flex items-center justify-center"
            >
              <svg class="animate-spin w-8 h-8 text-blue-600" fill="none" viewBox="0 0 24 24">
                <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
            </div>
          </div>

          <!-- Upload/Remove Buttons -->
          <div class="flex flex-col gap-2">
            <input
              ref="fileInputRef"
              type="file"
              accept="image/jpeg,image/jpg,image/png,image/webp"
              class="hidden"
              @change="handleFileSelect"
            />
            <button
              @click="triggerFileInput"
              :disabled="isUploading"
              class="inline-flex items-center px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              <Camera class="w-4 h-4 mr-2" />
              {{ currentAvatar ? 'Change Photo' : 'Upload Photo' }}
            </button>
            <button
              v-if="currentAvatar"
              @click="removePhoto"
              :disabled="isRemovingPhoto"
              class="inline-flex items-center px-4 py-2 bg-white border border-red-300 text-red-600 text-sm font-medium rounded-lg hover:bg-red-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              <Trash2 class="w-4 h-4 mr-2" />
              Remove
            </button>
          </div>
        </div>

        <p class="mt-4 text-sm text-gray-500">
          JPG, PNG, or WebP. Max size 5MB. You can drag and zoom to crop your photo.
        </p>
      </section>

      <!-- Personal Information Section -->
      <section class="bg-white rounded-2xl shadow-sm p-8">
        <h2 class="text-xl font-semibold text-gray-900 mb-6">Personal Information</h2>

        <form @submit.prevent="saveProfile" class="space-y-6">
          <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label class="block text-sm font-semibold text-gray-700 mb-2">First Name</label>
              <input
                v-model="form.first_name"
                type="text"
                class="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="John"
              />
              <p v-if="errors.first_name" class="mt-1 text-sm text-red-600">{{ errors.first_name[0] }}</p>
            </div>
            <div>
              <label class="block text-sm font-semibold text-gray-700 mb-2">Last Name</label>
              <input
                v-model="form.last_name"
                type="text"
                class="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="Smith"
              />
              <p v-if="errors.last_name" class="mt-1 text-sm text-red-600">{{ errors.last_name[0] }}</p>
            </div>
          </div>

          <div>
            <label class="block text-sm font-semibold text-gray-700 mb-2">Email Address</label>
            <input
              :value="user.email"
              type="email"
              disabled
              class="w-full px-4 py-3 border border-gray-200 rounded-xl bg-gray-50 text-gray-500 cursor-not-allowed"
            />
            <p class="mt-1 text-sm text-gray-500">Email cannot be changed</p>
          </div>

          <div>
            <label class="block text-sm font-semibold text-gray-700 mb-2">Phone Number</label>
            <PhoneInput v-model="form.phone" :invalid="Boolean(errors.phone)" />
            <p v-if="errors.phone" class="mt-1 text-sm text-red-600">{{ errors.phone[0] }}</p>
          </div>

          <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label class="block text-sm font-semibold text-gray-700 mb-2">Date of Birth</label>
              <input
                v-model="form.date_of_birth"
                type="date"
                class="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent appearance-none"
              />
              <p v-if="errors.date_of_birth" class="mt-1 text-sm text-red-600">{{ errors.date_of_birth[0] }}</p>
            </div>
            <div>
              <label class="block text-sm font-semibold text-gray-700 mb-2">Timezone</label>
              <select
                v-model="form.timezone"
                class="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option v-for="tz in timezones" :key="tz" :value="tz">{{ tz }}</option>
              </select>
            </div>
          </div>
        </form>
      </section>

      <!-- Save Button -->
      <div class="flex justify-end pt-6 border-t border-gray-200">
        <button
          @click="saveProfile"
          :disabled="isSaving"
          class="px-8 py-3.5 bg-gradient-to-r from-teal-500 to-teal-800 text-white rounded-xl font-bold hover:from-blue-700 hover:to-blue-800 transition-all shadow-xl shadow-blue-500/40 hover:shadow-2xl hover:shadow-blue-600/50 disabled:opacity-50 disabled:cursor-not-allowed flex items-center text-base"
        >
          <svg v-if="isSaving" class="animate-spin w-5 h-5 mr-2" fill="none" viewBox="0 0 24 24">
            <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
            <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
          {{ isSaving ? 'Saving...' : 'Save Changes' }}
        </button>
      </div>
    </div>

    <!-- Profile Picture Crop Modal -->
    <ProfilePictureCropModal
      :show="showCropModal"
      :image-file="selectedFile"
      :is-uploading="isUploading"
      @close="closeCropModal"
      @upload="handleCroppedUpload"
    />
  </StudentSettingsLayout>
</template>
